<template>
  <v-container class="profile-view">
    <v-card class="k-card profile-card pa-5" elevation="1">
      <div class="profile-name">
        {{ user ? user.displayName : "" }}
      </div>
      <div class="profile-email">
        {{ user ? user.email : "" }}
      </div>
    </v-card>

    <v-card class="k-card mt-4 pa-5" elevation="1">
      <div class="settings-header mb-3">
        <div class="text-h6 font-weight-bold">
          {{ $t("profile.settingsTitle") }}
        </div>
      </div>

      <div class="settings-list">
        <div class="settings-row">
          <div class="settings-icon">
            <v-icon color="primary">mdi-translate</v-icon>
          </div>

          <div class="settings-title">
            {{ $t("login.languageLabel") }}
          </div>

          <div class="settings-control">
            <LanguageSelector
              v-model="locale"
              :label="$t('login.languageLabel')"
              :disabled="deleting"
              hide-details
            />
          </div>
        </div>

        <v-divider class="settings-divider" />

        <div class="settings-row">
          <div class="settings-icon">
            <v-icon color="primary">mdi-bell-outline</v-icon>
          </div>

          <div class="settings-title">
            {{ $t("profile.notifications") }}
          </div>

          <div class="settings-control settings-switch">
            <v-switch
              v-model="eventInviteNotificationsEnabled"
              :loading="notificationsSaving"
              :disabled="notificationsLoading || notificationsSaving || !uid"
              inset
              hide-details
              @change="onToggleEventInviteNotifications"
            />
          </div>
        </div>

        <v-divider class="settings-divider" />

        <div class="settings-row">
          <div class="settings-icon">
            <v-icon color="primary">mdi-logout</v-icon>
          </div>

          <div class="settings-title">
            {{ $t("profile.logout") }}
          </div>

          <div class="settings-control">
            <v-btn color="primary" icon aria-label="logout" @click="doLogout">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="danger-zone">
          <div class="danger-title">
            {{ $t("profile.dangerZone") }}
          </div>

          <div class="settings-row danger-row">
            <div class="settings-icon">
              <v-icon color="error">mdi-delete-outline</v-icon>
            </div>

            <div class="settings-title error--text">
              {{ $t("profile.deleteAccount") }}
            </div>

            <div class="settings-control">
              <v-btn
                color="error"
                icon
                aria-label="delete account"
                @click="confirmDelete = true"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Dialogo confirmación -->
    <v-dialog v-model="confirmDelete" max-width="420">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t("profile.deleteAccountConfirmTitle") }}
        </v-card-title>

        <v-card-text>
          {{ $t("profile.deleteAccountConfirmBody") }}
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleting" @click="confirmDelete = false">
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            text
            :loading="deleting"
            :disabled="deleting"
            @click="deleteAccount"
          >
            {{ $t("common.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { Capacitor } from "@capacitor/core";
import { getAuth, deleteUser } from "firebase/auth";
import i18n from "@/i18n";
import LanguageSelector from "@/components/LanguageSelector.vue";

// ⬇️ AJUSTA esta ruta a donde tengas tu archivo
import {
  deleteMyAccountFirestoreData,
  getMyNotificationSettings,
  setEventInviteRequestNotifications,
} from "@/services/db";

export default {
  components: { LanguageSelector },
  data() {
    return {
      confirmDelete: false,
      deleting: false,
      locale: i18n.locale,
      notificationsLoading: false,
      notificationsSaving: false,
      eventInviteNotificationsEnabled: true,
    };
  },
  computed: {
    ...mapGetters("auth", ["user", "uid"]),
  },
  watch: {
    uid: {
      immediate: true,
      handler() {
        this.loadNotificationSettings();
      },
    },
  },
  methods: {
    signedOutRoute() {
      return Capacitor.isNativePlatform() ? "/login" : "/";
    },

    async doLogout() {
      await this.$store.dispatch("auth/logout");
      this.$router.replace(this.signedOutRoute());
    },

    async deleteAccount() {
      if (this.deleting) return;

      this.deleting = true;
      try {
        const auth = getAuth();
        const authUser = auth.currentUser;

        if (!authUser?.uid) {
          this.confirmDelete = false;
          return;
        }

        // 1) Limpieza Firestore
        await deleteMyAccountFirestoreData({ uid: authUser.uid });

        // 2) Borrado Auth
        await deleteUser(authUser);

        // 3) Logout + redirect
        await this.$store.dispatch("auth/logout");
        this.$router.replace(this.signedOutRoute());
      } catch (error) {
        if (error?.code === "auth/requires-recent-login") {
          alert(this.$t("profile.recentLoginNeeded"));
          await this.doLogout();
        } else {
          console.error(error);
          alert(this.$t("profile.deleteAccountError"));
        }
      } finally {
        this.deleting = false;
        this.confirmDelete = false;
      }
    },

    async loadNotificationSettings() {
      if (!this.uid) {
        this.eventInviteNotificationsEnabled = true;
        return;
      }

      this.notificationsLoading = true;
      try {
        const settings = await getMyNotificationSettings(this.uid);
        this.eventInviteNotificationsEnabled =
          settings.eventInviteRequests !== false;
      } catch (error) {
        console.error(error);
        this.eventInviteNotificationsEnabled = true;
      } finally {
        this.notificationsLoading = false;
      }
    },

    async onToggleEventInviteNotifications(enabled) {
      if (!this.uid || this.notificationsSaving) return;

      const previous = !enabled;
      this.notificationsSaving = true;
      try {
        await setEventInviteRequestNotifications({
          uid: this.uid,
          enabled,
        });
      } catch (error) {
        console.error(error);
        this.eventInviteNotificationsEnabled = previous;
        alert(this.$t("profile.saveNotificationError"));
      } finally {
        this.notificationsSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-view {
  max-width: 860px;
}

.profile-name {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.3;
}

.profile-email {
  font-size: 0.9rem;
  line-height: 1.35;
  margin-top: 4px;
  opacity: 0.72;
  word-break: break-word;
}

.settings-list {
  margin: 0 -6px;
}

.settings-row {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  min-height: 56px;
  padding: 6px;
}

.settings-icon {
  align-items: center;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.settings-title {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.25;
  min-width: 0;
}

.settings-control {
  justify-self: end;
  min-width: 0;
}

.settings-switch {
  min-width: 64px;
}

.settings-control .v-input {
  max-width: 180px;
}

.settings-divider {
  margin: 4px 0;
}

.danger-zone {
  background: rgba(244, 67, 54, 0.06);
  border: 1px solid rgba(244, 67, 54, 0.22);
  border-radius: 8px;
  margin-top: 14px;
  padding: 10px;
}

.danger-title {
  color: #d32f2f;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin: 0 0 2px 6px;
  text-transform: uppercase;
}

.danger-row {
  padding-bottom: 2px;
}

@media (max-width: 700px) {
  .profile-view {
    padding-left: 12px;
    padding-right: 12px;
  }

  .profile-card {
    padding: 16px !important;
  }

  .settings-row {
    grid-template-columns: 34px minmax(0, 1fr) auto;
    min-height: 54px;
  }

  .settings-icon {
    height: 34px;
    width: 34px;
  }

  .settings-control .v-input {
    max-width: 150px;
  }
}
</style>
